<template>
  <div
    :class="{
      'q-mb-sm': searchFilterTags.length > 0
    }"
  >
    <div class="row items-center q-gutter-sm">
      <q-chip
        v-for="(filterTagData, i) in searchFilterTags"
        :key="i + '_searchResultFilterTag'"
        :removable="filterTagData.removable !== false"
        icon-remove="sym_r_cancel"
        @remove="filterTagData.removeFunction"
        color="secondary"
        text-color="white"
        :disable="!$store.state.search.loaded"
      >
        <div class="flex items-center no-wrap" style="margin-bottom: -2px">
          <q-icon
            v-if="filterTagData.icon"
            :name="filterTagData.icon"
            size="16px"
            class="q-mr-xs"
          />
          <span class="q-mr-xs text-weight-medium">{{
            filterTagData.label
          }}</span>
        </div>
      </q-chip>
      <q-btn
        v-show="searchFilterTags.length > 0"
        class="--bordered text-weight-normal"
        rounded
        color="background"
        text-color="text-very-light"
        no-caps
        label="Clear All"
        :ripple="false"
        @click="resetFilters"
        style="padding: 3px 8px !important; min-height: 0; margin-bottom: 3px; font-weight: 400"
        :disable="!$store.state.search.loaded"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: "FilterTags",
  data() {
    return {
      searchFilterTags: []
    };
  },
  computed: {
    searchFilterModelsActive() {
      return this.$store.state.search.filter.modelsActive;
    },
    searchFilterOptions() {
      return this.$store.state.search.filter.options;
    },
    searchFiltersComplex() {
      return this.$store.state.search.searchFiltersComplex;
    }
  },
  watch: {
    searchFilterModelsActive: {
      handler() {
        this.generateSearchFilterTags();
      },
      deep: true
    },
    searchFilterOptions: {
      handler() {
        this.generateSearchFilterTags();
      },
      deep: true
    },
    searchFiltersComplex: {
      handler() {
        this.generateSearchFilterTags();
      },
      deep: true
    }
  },
  mounted() {
    this.generateSearchFilterTags();
  },
  methods: {
    generateSearchFilterTags() {
      let filterTags = [];
      let filterModelDefaults = this.$store.state.search.filter.modelDefaults;

      let vm = this;
      Object.keys(this.searchFilterModelsActive).forEach(modelKey => {
        let rangeModels = ["year", "mileage", "price"];
        if (rangeModels.includes(modelKey)) {
          let valueMin = this.searchFilterModelsActive[modelKey].min;
          let valueMax = this.searchFilterModelsActive[modelKey].max;
          if (
            valueMin === filterModelDefaults[modelKey].min &&
            valueMax === filterModelDefaults[modelKey].max
          ) {
            return;
          }

          if (modelKey === "mileage" || modelKey === "price") {
            if (valueMin !== null) {
              valueMin =
                (modelKey === "price" ? "$" : "") + this.formatNumber(valueMin);
            }
            if (valueMax !== null) {
              valueMax =
                (modelKey === "price" ? "$" : "") + this.formatNumber(valueMax);
            }
          }

          let label = "";
          if (valueMin !== null && valueMax !== null) {
            label =
              valueMin == valueMax ? valueMin : valueMin + " - " + valueMax;
          } else if (modelKey == "year") {
            if (valueMin) {
              label = valueMin + " and Newer";
            } else if (valueMax) {
              label = valueMax + " and Older";
            }
          } else {
            if (valueMin) {
              label = "Over " + valueMin;
            } else if (valueMax) {
              label = "Under " + valueMax;
            }
          }
          if (modelKey === "mileage") {
            label += " Miles";
          }

          filterTags.push({
            label: label,
            removeFunction: function() {
              vm.resetFilters(modelKey);
            }
          });
        } else {
          if (Array.isArray(this.searchFilterModelsActive[modelKey])) {
            this.searchFilterModelsActive[modelKey].forEach(filterValue => {
              let label = this.getFilterTagLabel(modelKey, filterValue);
              if (modelKey === "dealScorePhrase") {
                label = this.convertScoreToDealPhrase(
                  this.convertScorePhraseToScore(label)
                );
              }
              filterTags.push({
                label: label,
                removeFunction: function() {
                  vm.removeFilterValue(modelKey, filterValue);
                }
              });
            });
          } else if (
            this.searchFilterModelsActive[modelKey].length > 0 ||
            this.searchFilterModelsActive[modelKey] == 1
          ) {
            let label = this.searchFilterModelsActive[modelKey];
            if (modelKey.includes("features")) {
              label = this.getFilterTagLabel(modelKey, 1);
            }
            filterTags.push({
              label: label,
              removeFunction: function() {
                vm.removeFilterValue(modelKey, "");
              }
            });
          }
        }
      });

      // Add Smart Filters tag if there are complex filters
      if (this.searchFiltersComplex && this.searchFiltersComplex.length > 0) {
        const count = this.searchFiltersComplex.length;
        filterTags.push({
          label: `${count} Smart Filter${count > 1 ? "s" : ""}`,
          icon: "sym_r_auto_awesome",
          removable: true,
          removeFunction: () => {
            vm.removeComplexFilters();
          }
        });
      }

      this.searchFilterTags = filterTags;
    },
    getFilterTagLabel(modelKey, value) {
      if (modelKey === "makeId") {
        return this.$store.state.vehicleMakeModels.makeIdNameMap[value];
      } else if (modelKey === "modelId") {
        return (
          this.$store.state.vehicleMakeModels.makeIdNameMap[
            this.$store.state.vehicleMakeModels.modelIdMakeIdMap[value]
          ] +
          " " +
          this.$store.state.vehicleMakeModels.modelIdNameMap[value]
        );
      } else if (modelKey === "trim") {
        let [modelId, trim] = value.split("|");
        return (
          this.$store.state.vehicleMakeModels.modelIdNameMap[modelId] +
          " - " +
          trim
        );
      } else if (modelKey.includes("features")) {
        return modelKey
          .replace("features.", "")
          .replace(/([A-Z])/g, " $1")
          .replace(/^./, c => c.toUpperCase());
      } else {
        const option =
          modelKey in this.$store.state.search.filter.options
            ? this.$store.state.search.filter.options[modelKey].find(
                option => option.value == value
              )
            : null;

        return option ? option.label : null;
      }
    },
    removeFilterValue(modelKey, value) {
      // Directly manipulate filter.models, then apply to searchFilters
      const models = JSON.parse(
        JSON.stringify(this.$store.state.search.filter.models)
      );

      if (Array.isArray(models[modelKey])) {
        const valueIndex = models[modelKey].indexOf(value);
        if (valueIndex !== -1) {
          models[modelKey].splice(valueIndex, 1);
        }
      } else {
        models[modelKey] = "";
      }

      // Handle trim removal for modelId
      if (modelKey === "modelId" && models.trim) {
        models.trim = models.trim.filter(
          trimValue => !trimValue.includes(value + "|")
        );
      }

      // Update store models
      this.$store.state.search.filter.models = models;
      this.$store.state.search.filter.modelsActive = JSON.parse(
        JSON.stringify(models)
      );

      // Apply to searchFilters
      this.$store.dispatch("search/APPLY_FILTER_MODELS");

      this.resetSearch();
    },
    resetFilters(modelKey = "") {
      if (modelKey.length > 0) {
        // Reset specific filter
        const rangeModels = ["year", "mileage", "price"];
        const modelDefaults = this.$store.state.search.filter.modelDefaults;
        const models = JSON.parse(
          JSON.stringify(this.$store.state.search.filter.models)
        );

        if (rangeModels.includes(modelKey)) {
          models[modelKey].min = modelDefaults[modelKey].min;
          models[modelKey].max = modelDefaults[modelKey].max;
        } else {
          if (Array.isArray(modelDefaults[modelKey])) {
            models[modelKey] = JSON.parse(
              JSON.stringify(modelDefaults[modelKey])
            );
          } else {
            models[modelKey] = modelDefaults[modelKey];
          }
        }

        // Update store
        this.$store.state.search.filter.models = models;
        this.$store.state.search.filter.modelsActive = JSON.parse(
          JSON.stringify(models)
        );

        // Apply to searchFilters
        this.$store.dispatch("search/APPLY_FILTER_MODELS");
      } else {
        // Reset all filters
        this.$store.commit("search/SET_SEARCH_FILTERS", []);
      }

      this.resetSearch();
    },
    removeComplexFilters() {
      // Remove all complex filters from searchFilters
      this.$store.commit("search/REMOVE_COMPLEX_FILTERS");
      this.resetSearch();
    },
    resetSearch() {
      this.$store.commit("search/RESET_PAGINATION");
      this.$store.commit("search/SET_LOADING");
    }
  }
};
</script>

<style lang="scss" scoped></style>
